import { useEffect, useMemo, useState } from "react";
import { useGetMigrationQuery, useLoginRequestMutation, useMigrateDevicesMutation } from "../../../store/api/migration/migrationApiSlice";
import { useSelector } from "react-redux";
import { isConnectedSelector } from "../../../store/api/migration/selectors";
import { setDeviceLoginRequest, setInitialMigrattionData, setIsConnected } from "../../../store/api/migration/reducers";
import { useStoreDispatch } from "../../../store/hooks";
import { initialState } from "../../../store/api/migration/initialState";
import { IKeyValue } from "../../../components/Administrations/types";
import { ILoginRequest, IMigration } from "../../../store/api/migration/types";
import { setBasicDialogOpenFor, setIsLoadingStatus, setOpenToast, setPaginationModel } from "../../../store/helpers/helperReducers";
import { titleCase, errorMessage } from "../../../utils/helpers";
import { AppState } from "../../../store";
import { IGeoRegion } from "../../../store/api/admin/types";
import { selectedGeoRegionSiteGroupSelector, urlParameterSelector } from "../../../store/filters/Nodes/selectors";
import { dataGridSortSelector, paginationModelSelector } from "../../../store/helpers/helperSelectors";
import { API } from "../../../globals/globals";
import { searchKeywordSelector } from "../../../store/search/selector";
import { currentSiteGroupsSelector } from "../../../store/api/admin/selectors";
import { setSelectedDeviceStatus } from "../../../store/filters/Nodes/reducer";

export const useMigrationData = () => {
  const dispatch = useStoreDispatch();
  const defaultPage = useMemo(() => {
    return { page: 0, pageSize: 100 };
  }, []);
  const isConnected = useSelector(isConnectedSelector);
  const selectedGeoRegion = useSelector<AppState, IGeoRegion>(selectedGeoRegionSiteGroupSelector);
  const sortModel = useSelector(dataGridSortSelector);
  const filters = useSelector(urlParameterSelector);
  const currentSiteGroups = useSelector(currentSiteGroupsSelector);

  const keyword = useSelector(searchKeywordSelector);
  const { migration = defaultPage } = useSelector(paginationModelSelector);
  const [sortOrder, setSortOrderToMigration] = useState("created,desc");

  filters.set("sort", sortOrder);
  filters.set("page", migration.page.toString());
  filters.set("size", migration.pageSize.toString());
  filters.set("search", keyword);
  const args = { filters: filters.toString() };
  const { data, isLoading, refetch } = useGetMigrationQuery(args, {
    skip: sortModel === undefined || sortModel === "swVersion,desc",
    pollingInterval: API.ENDPOINTS.MIGRATION.pollingInterval || API.POLLING_INTERVAL,
  });

  const [migrateDevices, migrateStatus] = useMigrateDevicesMutation();
  const [loginData, setLoginData] = useState<ILoginRequest>(initialState.loginRequest);
  const [errMsg, setErrMsg] = useState<IKeyValue>({});
  const [siteMigrationData, setSiteMigrationData] = useState<IMigration[]>([]);
  const [loginRequest, loginStatus] = useLoginRequestMutation();
  const [siteGroupId, setSiteGroupId] = useState<string>("");
  const [selectedRow, setSelectedRow] = useState<IMigration[]>([]);

  useEffect(() => {
    dispatch(setIsLoadingStatus({ migrateStatus: migrateStatus.isLoading }));
  }, [dispatch, migrateStatus.isLoading]);

  useEffect(() => {
    dispatch(setIsConnected(false));
    dispatch(setDeviceLoginRequest(initialState.loginRequest));
    // dispatch(setDataGridSort({ migration: "created,desc" }));
    dispatch(setPaginationModel({ migration: defaultPage }));

    return () => {
      // dispatch(setDataGridSort({}));
      dispatch(setSelectedDeviceStatus(""));
    };
  }, [defaultPage, dispatch]);

  const onBlurHandler = (event: any) => {
    const { name, value } = event?.target;
    if (name === "hostAddress" && !!!value) {
      setErrMsg({ ...errMsg, [name]: `URL is required!` });
    } else {
      !!!value && setErrMsg({ ...errMsg, [name]: `${titleCase(name)} is required!` });
    }
  };

  const submitConnectHandler = async () => {
    Object.entries(loginData).forEach(([kay, value]) => {
      if (kay === "hostAddress" && !!!value) {
        setErrMsg({ ...errMsg, [kay]: `URL is required!` });
      } else {
        !!!value && setErrMsg({ ...errMsg, [kay]: `${titleCase(kay)} is required!` });
      }
    });
    try {
      let response: any;
      response = await loginRequest(loginData);
      if (response?.error) throw response?.error;
      dispatch(setDeviceLoginRequest(loginData));
      dispatch(setBasicDialogOpenFor(undefined));
      if (!!response?.data && response?.data.length > 0) {
        setSiteMigrationData(response?.data);
        dispatch(setInitialMigrattionData(response?.data));
      }
      dispatch(setIsConnected(true));
    } catch (error) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const startMigrationHandler = async () => {
    if (selectedRow.length > 0 && !!loginData) {
      const selectedDeviesToMigrate = selectedRow.map((device: IMigration) => ({
        name: device.name || "",
        ipAddress: device.ipAddress || "                -                                  ",
        key: device.serialNumber,
      }));
      const payload = { loginRequest: loginData, devices: selectedDeviesToMigrate, siteGroupId };
      try {
        let response: any;
        response = await migrateDevices(payload);
        if (response?.error) throw response?.error;
        dispatch(setBasicDialogOpenFor(undefined));
        dispatch(setIsConnected(false));
        dispatch(setDeviceLoginRequest(initialState.loginRequest));
        dispatch(setOpenToast({ openToast: true, toastType: "success", message: `Migration has been started` }));
        refetch();
      } catch (error) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    }
  };

  return {
    data,
    isLoading,
    isConnected,
    loginData,
    setLoginData,
    errMsg,
    setErrMsg,
    submitConnectHandler,
    loginStatus,
    siteMigrationData,
    setSiteMigrationData,
    onBlurHandler,
    currentSiteGroups,
    selectedGeoRegion,
    siteGroupId,
    setSiteGroupId,
    setSelectedRow,
    startMigrationHandler,
    setSortOrderToMigration,
  };
};
